/**
 * constants.js — shared step-type registry
 *
 * Each entry drives: the step palette, canvas node styling,
 * config form rendering, and the worker's execution dispatch.
 */

export const STEP_TYPES = {
  http_request: {
    label:       'HTTP Request',
    description: 'Fetch any URL with custom method, headers, and body',
    color:       '#3B82F6',
    cssVar:      '--color-http',
    icon:        'Globe',
    group:       'core',
    defaultConfig: {
      url:     '',
      method:  'GET',
      headers: {},
      body:    '',
    },
  },

  code: {
    label:       'Run Code',
    description: 'Execute custom JavaScript or TypeScript — access all previous step outputs',
    color:       '#A855F7',
    cssVar:      '--color-code',
    icon:        'Code2',
    group:       'core',
    defaultConfig: {
      language: 'javascript',   // 'javascript' | 'typescript'
      code: `// Access previous step data:
//   steps.trigger.data        — trigger payload
//   steps.myStep.output.status — previous step output
//
// Return an object to pass data to the next step.

return {
  message: "Hello from code step!",
  time: new Date().toISOString(),
};`,
    },
  },

  filter: {
    label:       'Filter',
    description: 'Stop the workflow if a JavaScript condition is not met',
    color:       '#F59E0B',
    cssVar:      '--color-filter',
    icon:        'Filter',
    group:       'core',
    defaultConfig: {
      condition: 'steps.trigger.data !== undefined',
    },
  },

  slack: {
    label:       'Slack Message',
    description: 'Send a message to a Slack channel via Incoming Webhook',
    color:       '#F97316',
    cssVar:      '--color-slack',
    icon:        'MessageSquare',
    group:       'integrations',
    defaultConfig: {
      webhook_url: '',
      message:     'New event: {{JSON.stringify(steps.trigger.data)}}',
      channel:     '',
      username:    'Workflow Bot',
      icon_emoji:  ':robot_face:',
    },
  },

  gmail: {
    label:       'Gmail',
    description: 'Send an email via the Gmail REST API (requires OAuth token)',
    color:       '#EF4444',
    cssVar:      '--color-gmail',
    icon:        'Mail',
    group:       'integrations',
    defaultConfig: {
      access_token: '',
      to:           '',
      subject:      '',
      body:         '',
      content_type: 'text/plain',
    },
  },
};

export const TRIGGER_TYPES = {
  webhook: {
    label:       'Webhook',
    description: 'Trigger via HTTP POST to a unique URL',
    icon:        'Webhook',
  },
  cron: {
    label:       'Schedule',
    description: 'Run on a cron schedule (e.g. every hour)',
    icon:        'Clock',
  },
  manual: {
    label:       'Manual',
    description: 'Trigger manually from the dashboard',
    icon:        'Play',
  },
};

export const STATUS_COLORS = {
  success: { bg: 'rgba(34,197,94,.15)',  text: '#22C55E' },
  error:   { bg: 'rgba(239,68,68,.15)',  text: '#EF4444' },
  running: { bg: 'rgba(34,211,238,.15)', text: '#22D3EE' },
};

export function statusBadge(status) {
  const map = { success: 'badge-success', error: 'badge-error', running: 'badge-running' };
  return map[status] ?? 'badge-pending';
}

export function fmtDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
